import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { InputPassword } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';

const passwordStepSchema = z.object({
  password: z.string().min(1, 'Enter a password to continue'),
});

export type IPasswordStepForm = z.infer<typeof passwordStepSchema>;

type Props = {
  email: string;
  onSubmit: (data: IPasswordStepForm) => Promise<void>;
  onBack: () => void;
};

const PasswordStepForm = ({ email, onSubmit, onBack }: Props) => {
  const form = useForm<IPasswordStepForm>({
    resolver: zodResolver(passwordStepSchema),
    defaultValues: { password: '' },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-1">
          <span className="block text-xs font-semibold text-muted-foreground">Continuing as</span>
          <span className="block text-sm font-semibold text-card-foreground">{email}</span>
        </div>

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Password</FormLabel>
              <FormControl>
                <InputPassword {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <p className="text-xs text-muted-foreground">
          New user? Use the temporary password from your invitation email.
        </p>

        <div className="flex justify-end">
          <Link to="/forgot-password" className="text-xs font-semibold text-foreground! hover:underline!">
            Forgot your password?
          </Link>
        </div>

        <div className="flex gap-2">
          <Button type="button" size="lg" variant="outline" className="flex-1" onClick={onBack}>
            Back
          </Button>
          <Button
            type="submit"
            size="lg"
            className="flex-1"
            isLoading={form.formState.isSubmitting}
            disabled={form.formState.isSubmitting}
          >
            Sign in
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default PasswordStepForm;
